import type { JoinRequestInfo } from "../networking/SignalingClient";
import { genderLabel } from "./PlayerIdentity";

type AnswerJoinRequest = (requestId: string, accepted: boolean) => Promise<void>;

export class JoinRequestPanel {
  private readonly requests = new Map<string, JoinRequestInfo>();
  private host = false;

  constructor(
    private readonly answer: AnswerJoinRequest,
    private readonly showError: (message: string) => void,
  ) {}

  add(request: JoinRequestInfo): void {
    this.requests.set(request.requestId, request);
    this.render();
  }

  remove(requestId: string): void {
    this.requests.delete(requestId);
    this.render();
  }

  clear(): void {
    this.requests.clear();
    this.render();
  }

  setHost(host: boolean): void {
    this.host = host;
    if (!host) this.requests.clear();
    this.render();
  }

  private render(): void {
    const panel = document.getElementById("join-requests");
    const list = document.getElementById("join-request-list");
    if (!panel || !list) return;
    const requests = this.host ? [...this.requests.values()] : [];
    panel.classList.toggle("hidden", requests.length === 0);
    list.innerHTML = requests.map(requestTemplate).join("");
    for (const button of list.querySelectorAll<HTMLButtonElement>("[data-request-action]")) {
      button.addEventListener("click", () => void this.respond(button));
    }
  }

  private async respond(button: HTMLButtonElement): Promise<void> {
    const requestId = button.dataset.requestId ?? "";
    if (!requestId) return;
    const row = document.querySelector<HTMLElement>(`[data-request-id="${requestId}"]`);
    for (const action of row?.querySelectorAll<HTMLButtonElement>("button") ?? []) action.disabled = true;
    try {
      await this.answer(requestId, button.dataset.requestAction === "accept");
      this.remove(requestId);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : "Could not answer the join request.");
      for (const action of row?.querySelectorAll<HTMLButtonElement>("button") ?? []) action.disabled = false;
    }
  }
}

const requestTemplate = (request: JoinRequestInfo): string => `
  <div class="join-request" data-request-id="${request.requestId}">
    <b>${escapeHtml(request.name)}<small>${genderLabel(request.gender)} wants to join</small></b>
    <button class="request-action decline" data-request-action="decline" data-request-id="${request.requestId}">Decline</button>
    <button class="request-action accept" data-request-action="accept" data-request-id="${request.requestId}">Accept</button>
  </div>`;

const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;",
})[character]!);
